import { ExternalLink } from 'lucide-react'
import { Drawer } from '@insightai/ui'
import { useCitationStore, useOpenCitation } from './citationStore'
import { ClientProvidedBadge, CredibilityBadge, LicensedDataBadge } from './CredibilityBadge'

function formatDate(iso?: string) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date)
}

/**
 * F0-29 / F0-33 — the single app-level citation drawer.
 *
 * Mounted ONCE, near the router root. Every `<CitedText>` superscript across
 * every module drives this one instance through the Zustand store.
 *
 * Content is the SRE-FR-005 contract: title, URL, publication date, retrieval
 * date, supporting passage and credibility band. Publication and retrieval dates
 * are shown separately and labelled — a 2019 report retrieved yesterday is a very
 * different claim from a report published yesterday, and collapsing them is how
 * a stale source gets read as current.
 */
export function CitationDrawer() {
  const citation = useOpenCitation()
  const close = useCitationStore((s) => s.close)

  return (
    <Drawer
      open={Boolean(citation)}
      onOpenChange={(open) => !open && close()}
      title="Source detail"
      description={citation ? `Citation ${citation.index}` : undefined}
      width="md"
    >
      {citation && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <h3 className="text-body font-medium text-text-primary">{citation.title}</h3>
            {citation.publisher && (
              <p className="text-body-sm text-text-secondary">{citation.publisher}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              {citation.credibility !== undefined && (
                <CredibilityBadge score={citation.credibility} />
              )}
              {citation.clientProvided && <ClientProvidedBadge />}
              {citation.licensed && <LicensedDataBadge note={citation.licenseNote} />}
            </div>
          </div>

          {citation.passage && (
            <figure className="flex flex-col gap-2">
              <figcaption className="text-micro uppercase tracking-wide text-text-tertiary">
                Supporting passage
              </figcaption>
              <blockquote className="border-s-2 border-brand-primary bg-surface-inset p-3 text-body-sm italic leading-relaxed text-text-primary">
                {citation.passage}
              </blockquote>
            </figure>
          )}

          <dl className="grid grid-cols-2 gap-3 text-body-sm">
            <div className="flex flex-col gap-0.5">
              <dt className="text-micro uppercase tracking-wide text-text-tertiary">Published</dt>
              <dd className="text-text-primary">{formatDate(citation.publishedAt) ?? 'Not stated'}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-micro uppercase tracking-wide text-text-tertiary">Retrieved</dt>
              <dd className="text-text-primary">{formatDate(citation.retrievedAt) ?? 'Not recorded'}</dd>
            </div>
          </dl>

          {citation.url && (
            <a
              href={citation.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 break-all text-body-sm text-text-link underline underline-offset-4 hover:text-brand-primary-hover"
            >
              {citation.url}
              <ExternalLink aria-hidden className="size-3.5 shrink-0" />
              <span className="sr-only">(opens in a new tab)</span>
            </a>
          )}
        </div>
      )}
    </Drawer>
  )
}
